import type { AuthUser } from "./api";

export type Role = "participant" | "creator";
export type View =
  | "dashboard"
  | "tournaments"
  | "catches"
  | "pending"
  | "announcements"
  | "messages"
  | "submit"
  | "create"
  | "account"
  | "settings"
  | "subscription"
  | "tournamentDashboard"
  | "terms"
  | "privacy"
  | "cookies"
  | "login"
  | "signup";

export const participantNav = ["Dashboard", "Tournaments", "My Catches", "Submit Catch"] as const;
export const creatorNav = ["Dashboard", "My Tournaments", "Review Submissions", "Create Tournament"] as const;

export const viewPaths: Record<View, string> = {
  dashboard: "/",
  tournaments: "/tournaments",
  catches: "/catches",
  pending: "/pending",
  announcements: "/announcements",
  messages: "/messages",
  submit: "/submit-catch",
  create: "/create-tournament",
  account: "/account",
  settings: "/settings",
  subscription: "/subscription",
  tournamentDashboard: "/tournament-dashboard",
  terms: "/terms-of-service",
  privacy: "/privacy-policy",
  cookies: "/cookie-policy",
  login: "/login",
  signup: "/signup",
};

export const appViewPaths: Partial<Record<View, string>> = {
  dashboard: "/app/dashboard",
  tournaments: "/app/tournaments",
  catches: "/app/catches",
  pending: "/app/review",
  announcements: "/app/announcements",
  messages: "/app/messages",
  submit: "/app/submit",
  create: "/app/create",
  account: "/app/account",
  settings: "/app/settings",
  subscription: "/app/subscription",
};

const viewPathAliases: Record<string, View> = {
  "/app": "dashboard",
  "/app/dashboard": "dashboard",
  "/app/tournaments": "tournaments",
  "/app/catches": "catches",
  "/app/submit": "submit",
  "/app/create": "create",
  "/app/manage": "tournaments",
  "/app/subscription": "subscription",
  "/app/account": "account",
  "/app/announcements": "announcements",
  "/app/notifications": "announcements",
  "/app/messages": "messages",
  "/app/review": "pending",
  "/app/settings": "settings",
};

export function canUseCreator(user: AuthUser | null) {
  return Boolean(user);
}

export function initialRoleForUser(user: AuthUser | null, savedRole: string | null): Role {
  if (!canUseCreator(user)) return "participant";
  return savedRole === "creator" ? "creator" : "participant";
}

export function navForRole(role: Role) {
  return role === "creator" ? creatorNav : participantNav;
}

export function primaryActionForRole(role: Role, hasCreatorAccess: boolean) {
  return role === "creator" && hasCreatorAccess
    ? { view: "create" as const, label: "Create Tournament" }
    : { view: "submit" as const, label: "Submit Catch" };
}

export function isOrganizerOnlyView(view: View) {
  return view === "pending" || view === "create";
}

export function normalizePathname(pathname: string) {
  return pathname.replace(/\/+$/, "") || "/";
}

export function tournamentIdFromPathname(pathname: string) {
  const normalized = normalizePathname(pathname);
  const match = normalized.match(/^\/app\/(?:tournaments|manage)\/([^/?#]+)$/);
  return match ? decodeURIComponent(match[1]) : "";
}

export function isAuthenticatedAppPath(pathname: string) {
  return normalizePathname(pathname).startsWith("/app");
}

export function roleHintFromPathname(pathname: string): Role | null {
  return /^\/app\/(?:manage|create)(?:\/|$)/.test(normalizePathname(pathname)) ? "creator" : null;
}

export function viewFromPathname(pathname: string): View {
  const normalized = normalizePathname(pathname);
  if (tournamentIdFromPathname(normalized)) return "tournamentDashboard";
  const found = (Object.entries(viewPaths) as Array<[View, string]>).find(([, path]) => path === normalized);
  return found?.[0] ?? viewPathAliases[normalized] ?? "dashboard";
}

export function pathForView(view: View, options: { authenticated: boolean; role?: Role | null }) {
  if (!options.authenticated) return viewPaths[view] ?? "/";
  if (view === "tournaments" && options.role === "creator") return "/app/manage";
  return appViewPaths[view] ?? viewPaths[view] ?? "/";
}
